import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useCRM } from '../../context/CRMContext';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Users, Briefcase,
  ChevronLeft, ChevronRight, ChevronDown,
  List, Sparkles, Upload, UserPlus, UserX, PlusCircle, BarChart3,
  PhoneCall, CreditCard, UserCog, FileText,
  Receipt, Link2, KeyRound, Settings2, MapPin, LifeBuoy, CalendarClock,
  Network, Lock, Building2, Home
} from 'lucide-react';
import './Sidebar.css';

// ── Count pill ─────────────────────────────────────────────
const CountPill = ({ value, variant }) => {
  if (value === undefined || value === null) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const label = n > 999 ? `${Math.floor(n / 1000)}k+` : String(n);
  return (
    <span className={`sidebar__count${variant ? ' sidebar__count--' + variant : ''}`}>{label}</span>
  );
};

// ─── Reusable collapsible group ───────────────────────────────────────────────
const SidebarGroup = ({ icon: Icon, label, basePath, subItems, sidebarOpen, parentCount }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(basePath);
  const [open, setOpen] = useState(isActive);

  return (
    <div className="sidebar__group">
      <button
        className={`sidebar__link sidebar__link--parent ${isActive ? 'sidebar__link--active' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        title={label}
      >
        <Icon size={20} />
        {sidebarOpen && (
          <>
            <span>{label}</span>
            <CountPill value={parentCount} />
            <ChevronDown
              size={16}
              className={`sidebar__chevron ${open ? 'sidebar__chevron--open' : ''}`}
            />
          </>
        )}
      </button>

      {sidebarOpen && open && (
        <div className="sidebar__submenu">
          {subItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end
              className={({ isActive }) =>
                `sidebar__sublink ${isActive ? 'sidebar__sublink--active' : ''}`
              }
            >
              <item.icon size={15} />
              <span>{item.label}</span>
              <CountPill value={item.count} variant="sub" />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Sidebar ─────────────────────────────────────────────────────────────
const Sidebar = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen, categoryCounts = {}, sidebarCounts = {} } = useCRM();
  const { hasPermission, isAdmin, user } = useAuth();
  const canSeeTeamHierarchy = isAdmin || ['team_leader', 'sales_manager'].includes(user?.role_slug);
  const location = useLocation();

  // On mobile, auto-close drawer after navigation so the page is visible.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
      if (sidebarOpen) setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const statusWiseCount = Array.isArray(categoryCounts.statusCounts)
    ? categoryCounts.statusCounts.reduce((s, x) => s + (Number(x.team_count) || 0), 0)
    : 0;

  // Total leads excluding imports — prefer backend field, else calc client-side.
  // Admin/Master see organic-only (Imported tab has its own count).
  // Non-admin sees full total (they don't have an Import tab — imported leads
  // assigned to them live inside Total Leads).
  const leadsExclImported = sidebarCounts.leadsTotalExcludingImported
    ?? Math.max(0, (Number(sidebarCounts.leadsTotal) || 0) - (Number(categoryCounts.imported) || 0));
  const totalLeadsCount = isAdmin ? leadsExclImported : (sidebarCounts.leadsTotal ?? leadsExclImported);

  const filterByPerm = (items) => items.filter(i => !i.perm || hasPermission(i.perm));

  const leadsSubItems = filterByPerm([
    { path: '/leads', icon: List, label: 'Total Leads', count: totalLeadsCount, perm: 'leads.view' },
    { path: '/leads/fresh', icon: Sparkles, label: 'Fresh Leads', count: categoryCounts.fresh, perm: 'leads.view' },
    { path: '/leads/imported', icon: Upload, label: 'Import Leads', count: categoryCounts.imported, perm: 'leads.import' },
    { path: '/leads/assigned', icon: UserPlus, label: 'Assign Leads', count: categoryCounts.assigned, perm: 'leads.assign' },
    { path: '/leads/non-assigned', icon: UserX, label: 'Non-Assign Leads', count: categoryCounts.nonAssigned, perm: 'leads.assign' },
    { path: '/leads/locked', icon: Lock, label: 'Locked Leads', perm: 'leads.view' },
    { path: '/leads/add', icon: PlusCircle, label: 'Add Lead', perm: 'leads.add' },
    { path: '/leads/status-wise', icon: BarChart3, label: 'Status Wise', count: statusWiseCount, perm: 'leads.view' },
    ...(isAdmin ? [{ path: '/lead-scheduling', icon: CalendarClock, label: 'Lead Scheduling' }] : []),
  ]);

  const loanSubItems = filterByPerm([
    { path: '/loans', icon: List, label: 'All Loans', count: sidebarCounts.loans, perm: 'loans.view' },
    { path: '/loans/add', icon: PlusCircle, label: 'Add Loan', perm: 'loans.add' },
  ]);

  const brokerSubItems = filterByPerm([
    { path: '/brokers/list', icon: List, label: 'Broker List', count: sidebarCounts.brokers, perm: 'brokers.view' },
    { path: '/brokers/add', icon: PlusCircle, label: 'Add Broker', perm: 'brokers.add' },
  ]);

  const hrSubItems = filterByPerm([
    { path: '/hr/candidates', icon: Users, label: 'All Candidates', count: sidebarCounts.candidates, perm: 'hr.view' },
    { path: '/hr/candidates/add', icon: PlusCircle, label: 'Add Candidate', perm: 'hr.add' },
  ]);

  const projectsSubItems = filterByPerm([
    { path: '/projects', icon: List, label: 'All Projects', count: sidebarCounts.projects, perm: 'projects.view' },
    { path: '/projects/add', icon: PlusCircle, label: 'Add Project', perm: 'projects.add' },
  ]);

  const propertiesSubItems = filterByPerm([
    { path: '/properties/all', icon: List, label: 'All Properties', count: sidebarCounts.properties, perm: 'properties.view' },
    { path: '/properties/add', icon: PlusCircle, label: 'Add Property', perm: 'properties.add' },
  ]);

  const reportsSubItems = hasPermission('reports.view') ? [
    { path: '/attendance/daily', icon: FileText, label: 'Attendance — Daily' },
    { path: '/attendance/monthly', icon: BarChart3, label: 'Attendance — Monthly' },
    { path: '/leads-report', icon: Users, label: 'Leads Report' },
  ] : [];

  const usersSubItems = isAdmin ? [
    { path: '/users/all', icon: List, label: 'All Users', count: sidebarCounts.users },
    { path: '/users/add', icon: UserPlus, label: 'Add User' },
  ] : [];

  return (
    <>
      <div
        className={`sidebar__backdrop ${sidebarOpen ? 'sidebar__backdrop--visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : 'sidebar--collapsed'}`}>

      {/* Header / Logo */}
      <div className="sidebar__header">
        {sidebarOpen && (
          <div className="sidebar__logo">
            <div className="sidebar__logo-icon">
              <img src={`${process.env.PUBLIC_URL}/favicon-dark.ico`} alt="LeaDBoat" />
            </div>
            <div className="sidebar__logo-text">
              <h1>LeaDBoat</h1>
              <span>Real Estate CRM</span>
            </div>
          </div>
        )}
        <button className="sidebar__toggle" onClick={toggleSidebar}>
          {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">

        {/* Dashboard */}
        <NavLink
          to="/"
          end
          className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
          title="Dashboard"
        >
          <LayoutDashboard size={20} />
          {sidebarOpen && <span>Dashboard</span>}
        </NavLink>

        {/* Leads */}
        {leadsSubItems.length > 0 && (
          <SidebarGroup
            icon={Users}
            label="Leads"
            basePath="/leads"
            subItems={leadsSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.leadsTotal}
          />
        )}

        {/* Follow up — visible to all users; page itself scopes data per-user */}
        <NavLink
          to="/followup"
          className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
          title="Follow up"
        >
          <PhoneCall size={20} />
          {sidebarOpen && (
            <>
              <span>Follow up</span>
              <CountPill value={sidebarCounts.followups} />
            </>
          )}
        </NavLink>

        {/* Loan */}
        {loanSubItems.length > 0 && (
          <SidebarGroup
            icon={CreditCard}
            label="Loan"
            basePath="/loans"
            subItems={loanSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.loans}
          />
        )}

        {/* All Broker */}
        {brokerSubItems.length > 0 && (
          <SidebarGroup
            icon={Briefcase}
            label="All Broker"
            basePath="/brokers"
            subItems={brokerSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.brokers}
          />
        )}

        {/* Projects */}
        {projectsSubItems.length > 0 && (
          <SidebarGroup
            icon={Building2}
            label="Projects"
            basePath="/projects"
            subItems={projectsSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.projects}
          />
        )}

        {/* Properties */}
        {propertiesSubItems.length > 0 && (
          <SidebarGroup
            icon={Home}
            label="Properties"
            basePath="/properties"
            subItems={propertiesSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.properties}
          />
        )}

        {/* Human Resources */}
        {hrSubItems.length > 0 && (
          <SidebarGroup
            icon={UserCog}
            label="Human Resources"
            basePath="/hr"
            subItems={hrSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.candidates}
          />
        )}

        {/* Reports */}
        {reportsSubItems.length > 0 && (
          <SidebarGroup
            icon={FileText}
            label="Reports"
            basePath="/attendance"
            subItems={reportsSubItems}
            sidebarOpen={sidebarOpen}
          />
        )}

        {/* Track Team */}
        {hasPermission('users.view_location') && (
          <NavLink
            to="/track-team"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="Track Team"
          >
            <MapPin size={20} />
            {sidebarOpen && <span>Track Team</span>}
          </NavLink>
        )}

        {/* Team Hierarchy */}
        {canSeeTeamHierarchy && (
          <NavLink
            to="/team"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="Team Hierarchy"
          >
            <Network size={20} />
            {sidebarOpen && <span>Team Hierarchy</span>}
          </NavLink>
        )}

        {/* Users */}
        {usersSubItems.length > 0 && (
          <SidebarGroup
            icon={Users}
            label="Users"
            basePath="/users"
            subItems={usersSubItems}
            sidebarOpen={sidebarOpen}
            parentCount={sidebarCounts.users}
          />
        )}

        {/* Dynamic Fields */}
        {hasPermission('dynamic_fields.view') && (
          <NavLink
            to="/dynamic-fields"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="Dynamic Fields"
          >
            <Settings2 size={20} />
            {sidebarOpen && <span>Dynamic Fields</span>}
          </NavLink>
        )}

        {/* PaySlip */}
        {isAdmin && (
          <NavLink
            to="/payslip"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="PaySlip"
          >
            <Receipt size={20} />
            {sidebarOpen && <span>PaySlip</span>}
          </NavLink>
        )}

        {/* API Integration */}
        {isAdmin && (
          <NavLink
            to="/api-integration"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="API Integration"
          >
            <Link2 size={20} />
            {sidebarOpen && <span>API Integration</span>}
          </NavLink>
        )}

        {/* Help Center */}
        <NavLink
          to="/help-center"
          className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
          title="Help Center"
        >
          <LifeBuoy size={20} />
          {sidebarOpen && (
            <>
              <span>Help Center</span>
              <CountPill value={sidebarCounts.helpCenter} />
            </>
          )}
        </NavLink>

        {/* Change Password */}
        {hasPermission('settings.change_password') && (
          <NavLink
            to="/change-password"
            className={({ isActive }) => `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`}
            title="Change Password"
          >
            <KeyRound size={20} />
            {sidebarOpen && <span>Change Password</span>}
          </NavLink>
        )}

      </nav>

      </aside>
    </>
  );
};

export default Sidebar;
